import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Box, Button, Input, Select, useDisclosure, useToast } from '@chakra-ui/react';
import { deleteBook, updateBook } from '../../services/api';
import { CampoForm } from '../../components/CampoForm';
import { ConfirmarExclusao } from '../../components/ConfirmarExclusao';
import { DrawerForm } from '../../components/DrawerForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { vazio } from '../../utils/validacao';
import { toBase64, uploadWithBase64 } from '../../utils/imageHelper';
import { BookGet } from '.';

type DrawerCreateProps = {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
	afterClose: () => void;
	bookUpdate: BookGet;
};

type Campo = 'nome' | 'descricao' | 'capa';

export function DrawerUpdate({ isOpen, onClose, afterClose, bookUpdate }: DrawerCreateProps) {
	const toast = useToast();
	const confirmacao = useDisclosure();
	const { erros, validar, limparErro, limparTudo } = useErrosForm<Campo>();

	const [ loading, setLoading ] = useState(false);
	// Guarda se a capa foi trocada nesta edicao: sem troca, reenviamos a url que ja
	// esta salva em vez de subir a imagem de novo.
	const [ capaAlterada, setCapaAlterada ] = useState(false);

	const [ nome, setNome ] = useState('');
	const [ descricao, setDescricao ] = useState('');
	const [ capa, setCapa ] = useState('');
	const [ idioma, setIdioma ] = useState('');
	const [ nivel, setNivel ] = useState('');

	const resetForm = useCallback(
		() => {
			setCapa(bookUpdate.capa);
			setDescricao(bookUpdate.descricao);
			setIdioma(bookUpdate.idioma);
			setNivel(bookUpdate.nivel);
			setNome(bookUpdate.nome);
			setCapaAlterada(false);
			limparTudo();
		},
		[ bookUpdate, limparTudo ]
	);

	useEffect(
		() => {
			if (isOpen) resetForm();
		},
		[ isOpen, resetForm ]
	);

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (vazio(nome)) encontrados.nome = 'Nome do book é obrigatório!';
		if (vazio(descricao)) encontrados.descricao = 'Descrição é obrigatória!';
		if (!capa) encontrados.capa = 'Capa é obrigatória!';

		return encontrados;
	};

	const handleSalvar = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!validar(montarErros())) return;

		setLoading(true);
		const capaPronta = capaAlterada ? uploadWithBase64(capa) : Promise.resolve(capa);

		capaPronta
			.then((link) =>
				updateBook(`${bookUpdate.id}`, {
					capa: link,
					descricao: descricao.trim(),
					idioma,
					nivel,
					nome: nome.trim()
				})
			)
			.then(() => {
				setLoading(false);
				toast({
					title: `Livro atualizado com sucesso!`,
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				const message = error?.response?.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível atualizar o livro.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleExcluir = () => {
		confirmacao.onClose();
		setLoading(true);
		deleteBook(`${bookUpdate.id}`)
			.then(() => {
				setLoading(false);
				toast({
					title: `Livro removido com sucesso.`,
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				const message = error?.response?.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível remover o livro.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleSelectedFile = async (files: FileList | null) => {
		if (files && files[0] && files[0].size < 10000000) {
			const picBase64 = await toBase64(files[0]);
			setCapa(`${picBase64}`);
			setCapaAlterada(true);
			limparErro('capa');
		} else {
			toast({
				title: `Foto muito pesada, selecione outra!`,
				status: 'warning',
				isClosable: true
			});
		}
	};

	const handleFechar = () => {
		resetForm();
		onClose();
		afterClose();
	};

	return (
		<>
			<DrawerForm
				isOpen={isOpen}
				onClose={handleFechar}
				titulo="Editar Book"
				formId="form-editar-book"
				loading={loading}
				loadingLabel="Salvando..."
				onSubmit={handleSalvar}
				acaoDestrutiva={
					<Button colorScheme="red" variant="outline" isDisabled={loading} onClick={confirmacao.onOpen}>
						Excluir
					</Button>
				}
			>
				<CampoForm label="Nome do Book" htmlFor="nome" erro={erros.nome}>
					<Input
						id="nome"
						placeholder="Insira o Nome do book"
						onChange={(e) => {
							setNome(e.target.value);
							limparErro('nome');
						}}
						value={nome}
					/>
				</CampoForm>

				<CampoForm label="Descrição" htmlFor="descricao" erro={erros.descricao}>
					<Input
						id="descricao"
						placeholder="Insira a Descrição"
						onChange={(e) => {
							setDescricao(e.target.value);
							limparErro('descricao');
						}}
						value={descricao}
					/>
				</CampoForm>

				<CampoForm label="Idioma" htmlFor="idioma">
					<Select id="idioma" onChange={(e) => setIdioma(e.target.value)} value={idioma}>
						<option value="Inglês">Inglês</option>
						<option value="Português">Português</option>
						<option value="Italiano">Italiano</option>
						<option value="Espanhol">Espanhol</option>
						<option value="Francês">Francês</option>
					</Select>
				</CampoForm>

				<CampoForm label="Nível" htmlFor="nivel">
					<Select id="nivel" onChange={(e) => setNivel(e.target.value)} value={nivel}>
						<option value="Beginner">Beginner</option>
						<option value="Intermediate">Intermediate</option>
						<option value="Advanced">Advanced</option>
					</Select>
				</CampoForm>

				<Box
					width="100%"
					height="150px"
					borderWidth="1px"
					borderColor="gray.200"
					borderRadius="10px"
					bgPosition="center"
					bgSize="cover"
					bgImage={capa}
				/>

				<CampoForm label="Capa" htmlFor="capa" erro={erros.capa}>
					<Input
						id="capa"
						type="file"
						accept="image/*"
						paddingTop="4px"
						onChange={(files) => handleSelectedFile(files.target.files)}
					/>
				</CampoForm>
			</DrawerForm>

			<ConfirmarExclusao
				isOpen={confirmacao.isOpen}
				onClose={confirmacao.onClose}
				onConfirmar={handleExcluir}
				titulo="Excluir book"
				descricao={`Tem certeza que deseja excluir ${bookUpdate.nome}? As lessons vinculadas também serão removidas.`}
			/>
		</>
	);
}
